import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { t } from 'i18next';
import { Measurements, PickupDetails, ShipmentSummary } from './ContentBox';
import Accessorials from '../../../lib/ui/form_elements/ContentBoxes/Accessorials/Accessorials';
import { baseServiceLevelCode, PaymentMethods } from '../../../utils/enums';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import Edit from '../../../assets/icons/Edit.svg';
import InformationOrange from '../../../assets/icons/InformationOrange.svg';
import {
  CategoryTitle,
  Icon,
  LineButton,
  UploadedDocumentsSummary,
  UploadedDocumentsInformation,
  FieldGroupLabel,
  DisclaimerGroup,
  DisclaimerNote,
  DisclaimerText,
} from '../css';
import {
  formatMoney,
  formatDisplayDateHuman,
  isCanadianAddress,
} from '../../../utils/func';
import RateCard from '../../ReactComponent/RateCard/RateCard';
import UploadDocuments from '../../ReactComponent/UploadDocuments';
import UploadDocumentsRenderModes from '../../ReactComponent/UploadDocuments/constants';

export const SummaryForm = ({
  formData = {},
  editStepOne,
  editStepTwo,
  editStepThree,
  editStepFour,
  parentFields,
}) => (
  <>
    <CategoryTitle>
      {parentFields.step5CategoryTitle1 && parentFields.step5CategoryTitle1.value}
      {editStepFour && (
        <LineButton onClick={editStepFour}>
          <p>{t('Edit')}</p>
          <Icon className="icon right" src={Edit} alt="edit step one" />
        </LineButton>
      )}
    </CategoryTitle>
    <PickupDetails formData={formData} />

    {/* --- Uploaded Documents --- */}
    <CategoryTitle>
      {parentFields.uploadedDocumentsHeader.value}
      {editStepFour && (
        <LineButton onClick={editStepFour}>
          <p>{t('Edit')}</p>
          <Icon className="icon right" src={Edit} alt="edit step four" />
        </LineButton>
      )}
    </CategoryTitle>
    {editStepFour ? (
      <>
        <UploadedDocumentsSummary field={parentFields.uploadedDocumentsSummary} />
        <UploadDocuments
          numberOfPlaceholders={4}
          renderMode={UploadDocumentsRenderModes.REVIEW}
          attachedDocuments={formData && formData.attachedDocuments}
        />
      </>
    ) : (
      <>
        <UploadedDocumentsInformation>
          <Icon className="icon" src={InformationOrange} alt="information" />
          <UploadedDocumentsSummary field={parentFields.uploadedDocumentsManagePage} />
        </UploadedDocumentsInformation>
        <UploadDocuments
          numberOfPlaceholders={4}
          renderMode={UploadDocumentsRenderModes.REVIEW}
          attachedDocuments={formData && formData.attachedDocuments}
        />
      </>
    )}

    <CategoryTitle>
      {parentFields.step5CategoryTitle2 && parentFields.step5CategoryTitle2.value}
      {editStepOne && (
        <LineButton onClick={editStepOne}>
          <p>{t('Edit')}</p>
          <Icon className="icon right" src={Edit} alt="edit step one" />
        </LineButton>
      )}
    </CategoryTitle>
    <ShipmentSummary formData={formData} />

    <CategoryTitle>
      {parentFields.step5CategoryTitle3 && parentFields.step5CategoryTitle3.value}
      {editStepTwo && (
        <LineButton onClick={editStepTwo}>
          <p>{t('Edit')}</p>
          <Icon className="icon right" src={Edit} alt="edit step two" />
        </LineButton>
      )}
    </CategoryTitle>
    <Measurements formData={formData} />

    <CategoryTitle>
      {parentFields.step5CategoryTitle4 && parentFields.step5CategoryTitle4.value}
      {editStepThree && (
        <LineButton onClick={editStepThree}>
          <p>{t('Edit')}</p>
          <Icon className="icon right" src={Edit} alt="edit step three" />
        </LineButton>
      )}
    </CategoryTitle>
    <Accessorials formData={formData} />
  </>
);

const Step5 = ({
  formData = {},
  serviceLevelCode,
  accessorialsBase,
  step,
  editStepOne,
  editStepTwo,
  editStepThree,
  serviceLevelQuotes,
  putReduxServiceLevelCode,
  editStepFour,
  parentFields,
  billingAccounts,
  serviceLevels,
}) => {
  const {
    shipperResidentialAddress, consigneeResidentialAddress, shipperAddress, consigneeAddress,
    shipperCountry, consigneeCountry, shipperPostalZipCode, consigneePostalZipCode,
  } = formData;
  let protectedAccount = billingAccounts && billingAccounts.some((a) => a.accountNumber === formData.billTo && a.protected);

  // When "Add New" is used, the typed account number won't exist in billingAccounts — always protect rates
  // Credit Card payments use system-assigned bill-to accounts, so rates should remain visible
  if (formData.paymentMethod !== PaymentMethods.CreditCard
    && formData.billTo && billingAccounts && !billingAccounts.some((a) => a.accountNumber === formData.billTo)) {
    protectedAccount = true;
  }

  if (formData && formData.division === 'Sameday' && formData.paymentMethod) {
    if (formData.paymentMethod === 'Collect' || (formData.paymentMethod === 'Third Party'
      && (protectedAccount || formData.billToMethod === 'EnterBillTo'))) {
      protectedAccount = true;
    }
  }

  const allServicesWouldBeHidden = serviceLevelQuotes.length > 0
    && serviceLevelQuotes.every((item) => {
      const totalAmountValue = (item.totalAmount && item.totalAmount.value) ? item.totalAmount.value : 0;
      return totalAmountValue === 0
        || item.isApproved === false
        || ((item.serviceLevelCode === 'R2R' || item.serviceLevelCode === 'R2D')
          && shipperResidentialAddress && !consigneeResidentialAddress);
    });

  // When all services would be hidden, pick base services to show
  // Always include R2T alongside the primary fallback so vendors can select 2-person delivery
  const baseServicePriority = [
    baseServiceLevelCode.residencial1,
    baseServiceLevelCode.domesticLTL,
    baseServiceLevelCode.transborderLTL,
    baseServiceLevelCode.residencial2,
    baseServiceLevelCode.residencial3,
  ];
  const primaryFallbackCode = allServicesWouldBeHidden
    ? baseServicePriority.find((code) => serviceLevelQuotes.some((s) => s.serviceLevelCode === code))
    : null;
  const fallbackServiceCodes = allServicesWouldBeHidden
    ? [primaryFallbackCode, baseServiceLevelCode.residencial2].filter(
      (code) => code && serviceLevelQuotes.some((s) => s.serviceLevelCode === code),
    )
    : [];

  const serviceLevelOptions = serviceLevelQuotes.map((item) => {
    const chargesObj = item.charges.map((charge) => {
      const amountValue = (charge.amount && charge.amount.value) ? charge.amount.value : 0;
      const filteredArr = accessorialsBase.filter((item) => item.code === charge.chargeCode);
      const chargeDesc = (filteredArr.length > 0) ? filteredArr[0].description : charge.description;

      return { amount: formatMoney(amountValue, protectedAccount), description: chargeDesc, amountValue };
    });

    const totalChargesValue = (item.totalCharges && item.totalCharges.value) ? item.totalCharges.value : 0;
    const taxValue = (item.tax && item.tax.value) ? item.tax.value : 0;
    const totalAmountValue = (item.totalAmount && item.totalAmount.value) ? item.totalAmount.value : 0;
    const totalAmountCurrencyCode = (item.totalAmount && item.totalAmount.currencyCode) ? item.totalAmount.currencyCode : '';

    const showAsUnapproved = fallbackServiceCodes.includes(item.serviceLevelCode);

    const privateResidenceDelivery = formData?.accessorials?.accPRESDL ?? '';
    const privateResidencePickup = formData?.accessorials?.accPRESPU ?? '';

    const isShipperCanada = isCanadianAddress(shipperAddress, shipperCountry, shipperPostalZipCode);
    const isConsigneeCanada = isCanadianAddress(consigneeAddress, consigneeCountry, consigneePostalZipCode);

    const isResidentialCondition = isShipperCanada && isConsigneeCanada
      && (
        shipperResidentialAddress
        || consigneeResidentialAddress
        || privateResidenceDelivery?.selected
        || privateResidencePickup?.selected
      );

    const residentialServices = ['R1T', 'R2T', 'R1NS'];
    const isResidentialService = residentialServices.includes(item.serviceLevelCode);
    const isResidentialServiceVisible = isResidentialCondition && isResidentialService;

    return (
      {
        label: item.description,
        value: item.serviceLevelCode,
        charges: chargesObj,
        expDate: item.expectedDeliveryDate ? formatDisplayDateHuman(new Date(item.expectedDeliveryDate)) : '',
        totalCharges: formatMoney(totalChargesValue, protectedAccount),
        totalChargesValue,
        tax: formatMoney(taxValue, protectedAccount),
        taxValue,
        totalAmount: formatMoney(totalAmountValue, protectedAccount),
        defaultOption: serviceLevelCode === item.serviceLevelCode,
        toBeHidden: (() => {
          if (showAsUnapproved) return false;

          if (isResidentialServiceVisible) return false;

          if (isResidentialService && !isResidentialCondition) return true;

          if (totalAmountValue === 0) return true;

          if (item.isApproved === false) return true;

          if (
            (item.serviceLevelCode === 'R2R' || item.serviceLevelCode === 'R2D')
            && shipperResidentialAddress
            && !consigneeResidentialAddress
          ) return true;

          return false;
        })(),
        noServicesAvailable: showAsUnapproved,
        totalAmountCurrencyCode,
        totalAmountValue,
        isApproved: item.isApproved,
      }
    );
  });

  if (step === 4) {
    return (
      <>
        <Row>
          <Column $mobile={12}>
            <FieldGroupLabel>{t('Options')}</FieldGroupLabel>
            <DisclaimerGroup>
              <DisclaimerNote>{t('Note')}</DisclaimerNote>
              <DisclaimerText>
                :
                {t('Disclaimer')}
              </DisclaimerText>
            </DisclaimerGroup>

            <Field
              name="rateReview"
              component={RateCard}
              options={serviceLevelOptions}
              current={serviceLevelCode}
              putReduxServiceLevelCode={putReduxServiceLevelCode}
              serviceLevels={serviceLevels}
              page="create-shipment"
            />
          </Column>
        </Row>

        <SummaryForm
          formData={formData}
          editStepOne={editStepOne}
          editStepTwo={editStepTwo}
          editStepThree={editStepThree}
          editStepFour={editStepFour}
          parentFields={parentFields}
        />
      </>
    );
  }

  return null;
};

SummaryForm.propTypes = {
  formData: PropTypes.object,
  editStepOne: PropTypes.func,
  editStepTwo: PropTypes.func,
  editStepThree: PropTypes.func,
  editStepFour: PropTypes.func,
  parentFields: PropTypes.object,
};
Step5.propTypes = {
  formData: PropTypes.object,
  serviceLevelCode: PropTypes.string,
  accessorialsBase: PropTypes.string,
  step: PropTypes.number,
  editStepOne: PropTypes.func,
  editStepTwo: PropTypes.func,
  editStepThree: PropTypes.func,
  editStepFour: PropTypes.func,
  filter: PropTypes.func,
  serviceLevelQuotes: PropTypes.array,
  putReduxServiceLevelCode: PropTypes.func,
  parentFields: PropTypes.object,
  billingAccounts: PropTypes.array,
  serviceLevels: PropTypes.string,
};

export default Step5;
